import { create } from "zustand";
import { fetchSite, fetchSiteFeatures } from "../api/siteid";
import { fetchWithAuth } from "../api/fetchWithAuth";

import { getConfig } from '../config';

const apiBase = () => getConfig().siteidApiBase ?? "/siteid";

// ---------------------------------------------------------------------------
// Zoom thresholds — must match ZOOM constants in sitesRoutes.js
// ---------------------------------------------------------------------------
const ZOOM = {
  CLUSTER_MAX: 12,   // z ≤ 12 → clusters (centroid points fed to MapLibre)
  SIMPLE_MAX:  14,   // z 13–14 → bbox rectangle polygons
  // z 15+        → simplified full polygon
  // on selection → full polygon regardless of zoom (fetched via /sites/:id)
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export const useSiteStore = create((set, get) => ({

  // --- Feature flags ---
  siteIdEnabled: false,

  // --- Project context ---
  activeProjectId: null,

  // --- Viewport state ---
  viewport: null,   // { minLng, minLat, maxLng, maxLat, zoom }

  // --- Site data ---
  siteCollection: { type: "FeatureCollection", features: [] },

  // --- Selection ---
  selectedSiteId:     null,
  selectedSiteDetail: null,
  siteFeatures:       null,

  // --- Focused feature (fly-to from panel) ---
  focusedFeatureId: null,

  // --- Async state ---
  loadingViewport: false,
  loadingDetail:   false,
  viewportError:   null,
  detailError:     null,

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  toggleSiteId: () =>
    set((state) => ({ siteIdEnabled: !state.siteIdEnabled })),

  setActiveProject: (projectId) =>
    set({
      activeProjectId: projectId,
      siteCollection:  { type: "FeatureCollection", features: [] },
      selectedSiteId:     null,
      selectedSiteDetail: null,
      siteFeatures:       null,
      viewport:           null,
    }),

  /**
   * Called by SiteLayer on every map moveend/zoomend (after debounce).
   * Always fetches — server returns appropriate geometry for the zoom level:
   *   z ≤ 12  → centroid points  (for MapLibre cluster source)
   *   z 13–14 → bbox rectangles  (for polygon fill source)
   *   z 15+   → simplified polys (for polygon fill source)
   */
  fetchViewport: async (viewport) => {
    const { siteIdEnabled, activeProjectId } = get();

    if (!siteIdEnabled || !activeProjectId) {
      console.warn("[fetchViewport] blocked —", { siteIdEnabled, activeProjectId });
      return;
    }

    set({ viewport, loadingViewport: true, viewportError: null });

    const { minLng, minLat, maxLng, maxLat, zoom } = viewport;

    const params = new URLSearchParams({
      minLng, minLat, maxLng, maxLat, zoom,
      project_id: activeProjectId,
    });

    try {
      const res = await fetchWithAuth(`${apiBase()}/sites/viewport?${params}`, {
        credentials: "include",
      });

      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useSiteStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      const featureCollection = await res.json();
      set({ siteCollection: featureCollection, loadingViewport: false });

    } catch (err) {
      console.error("[useSiteStore] fetchViewport", err);
      set({ loadingViewport: false, viewportError: err.message });
    }
  },

  /**
   * Called by SiteLayer's click handler.
   * Immediately records the selected id (panel opens with skeleton),
   * then fetches full detail + features in parallel.
   * The detail response always includes the full unmodified polygon geometry.
   */
  selectSite: async (siteId) => {
    if (!siteId) return get().clearSelection();

    set({
      selectedSiteId:     siteId,
      selectedSiteDetail: null,
      siteFeatures:       null,
      loadingDetail:      true,
      detailError:        null,
    });

    try {
      const [detailRes, featuresRes] = await Promise.all([
        fetchWithAuth(`${apiBase()}/sites/${siteId}`,          { credentials: "include" }),
        fetchWithAuth(`${apiBase()}/sites/${siteId}/features`, { credentials: "include" }),
      ]);

      if (!detailRes.ok || !featuresRes.ok) {
        throw new Error(`Failed to load site ${siteId}`);
      }

      const [detail, features] = await Promise.all([
        detailRes.json(),
        featuresRes.json(),
      ]);

      set({
        selectedSiteDetail: detail,
        siteFeatures:       features,
        loadingDetail:      false,
      });

    } catch (err) {
      console.error("[useSiteStore] selectSite", err);
      set({ loadingDetail: false, detailError: err.message });
    }
  },

  focusFeature: (featureId) => set({ focusedFeatureId: featureId }),
  clearFocusedFeature: () => set({ focusedFeatureId: null }),

  clearSelection: () =>
    set({
      selectedSiteId:     null,
      selectedSiteDetail: null,
      siteFeatures:       null,
      detailError:        null,
    }),

  // ---------------------------------------------------------------------------
  // Derived helpers
  // ---------------------------------------------------------------------------

  /** True when the current zoom should render clusters */
  isClustering: () => {
    const { viewport } = get();
    return viewport ? viewport.zoom <= ZOOM.CLUSTER_MAX : true;
  },

  /** True when bbox rectangles are being shown (z13–14) */
  isSimplePolys: () => {
    const { viewport } = get();
    return viewport
      ? viewport.zoom > ZOOM.CLUSTER_MAX && viewport.zoom <= ZOOM.SIMPLE_MAX
      : false;
  },

  /** True when simplified full polygons are shown (z15+) */
  showPolygons: () => {
    const { viewport } = get();
    return viewport ? viewport.zoom > ZOOM.SIMPLE_MAX : false;
  },

}));

export { ZOOM };
